
import { useEffect, useState } from "react";
import ParticlesBackground from "../../components/ParticlesBackground";
import "./Search.css";
import SearchForm from "./SearchForm";
import SearchTable from "./SearchTable";

const SEARCH_SESSION_STORAGE_KEY = "licitacionesSearchState";
const SEARCH_SESSION_TTL_MS = 12 * 60 * 60 * 1000;

function buildFiltersWithQuery(filters = {}, query = "") {
  const nextFilters = { ...(filters || {}) };
  const trimmedQuery = String(query || "").trim();

  if (trimmedQuery) {
    nextFilters.q = trimmedQuery;
  } else {
    delete nextFilters.q;
  }

  return nextFilters;
}

function loadStoredSearchState() {
  if (typeof window === "undefined") {
    return { filters: {}, searchQuery: "" };
  }

  try {
    const raw = window.localStorage.getItem(SEARCH_SESSION_STORAGE_KEY);
    if (!raw) {
      return { filters: {}, searchQuery: "" };
    }

    const parsed = JSON.parse(raw);
    const expiresAt = Number(parsed?.expiresAt || 0);
    if (!expiresAt || Date.now() > expiresAt) {
      window.localStorage.removeItem(SEARCH_SESSION_STORAGE_KEY);
      return { filters: {}, searchQuery: "" };
    }

    return {
      filters: typeof parsed.filters === "object" && parsed.filters ? parsed.filters : {},
      searchQuery: typeof parsed.searchQuery === "string" ? parsed.searchQuery : "",
    };
  } catch (error) {
    window.localStorage.removeItem(SEARCH_SESSION_STORAGE_KEY);
    return { filters: {}, searchQuery: "" };
  }
}

function persistSearchState(filters, searchQuery) {
  if (typeof window === "undefined") {
    return;
  }

  const normalizedFilters = typeof filters === "object" && filters ? filters : {};
  const normalizedQuery = String(searchQuery || "").trim();
  const hasFilters = Object.keys(normalizedFilters).length > 0;

  if (!hasFilters && !normalizedQuery) {
    window.localStorage.removeItem(SEARCH_SESSION_STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(
    SEARCH_SESSION_STORAGE_KEY,
    JSON.stringify({
      filters: normalizedFilters,
      searchQuery: normalizedQuery,
      expiresAt: Date.now() + SEARCH_SESSION_TTL_MS,
    })
  );
}

function Search() {
  const [initialSearchState] = useState(() => loadStoredSearchState());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState(initialSearchState.filters);
  const [searchQuery, setSearchQuery] = useState(initialSearchState.searchQuery);
  
  // Estados para filtros guardados
  const [showSaveFilterModal, setShowSaveFilterModal] = useState(false);
  const [showLoadFilterModal, setShowLoadFilterModal] = useState(false);
  const [savedFilters, setSavedFilters] = useState([]);
  const [filterName, setFilterName] = useState('');
  const [filterDescription, setFilterDescription] = useState('');

  // Función para obtener el token CSRF
  const getCsrfToken = () => {
    const name = 'csrftoken';
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  };
  
  // Estados para paginación
  const [paginationInfo, setPaginationInfo] = useState({
    count: 0,
    has_next: false,
    has_previous: false,
    current_page: 1,
    total_pages: 1,
    limit: 100,
    offset: 0
  });

  // Nueva función para construir la URL con filtros y paginación
  const fetchLicitaciones = async (filters = {}, page = 1) => {
    try {
      setLoading(true);
      setError(null);
      let url = '/api/analytics/licitaciones/';
      const params = new URLSearchParams();
      
      // Agregar paginación
      const limit = 100;
      const offset = (page - 1) * limit;
      params.append('limit', limit);
      params.append('offset', offset);
      
      // Agregar filtros
      Object.entries(filters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value
            .map(item => String(item).trim())
            .filter(Boolean)
            .forEach(item => params.append(key, item));
          return;
        }

        if (value !== undefined && value !== null && value !== "") {
          params.append(key, value);
        }
      });
      
      url += `?${params.toString()}`;
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      const result = await response.json();
      
      // Manejar respuesta paginada
      if (result.results && Array.isArray(result.results)) {
        setData(result.results);
        setPaginationInfo({
          count: result.count,
          has_next: result.has_next,
          has_previous: result.has_previous,
          current_page: result.current_page,
          total_pages: result.total_pages,
          limit: result.limit,
          offset: result.offset
        });
      } else if (Array.isArray(result)) {
        // Fallback para compatibilidad
        setData(result);
      } else {
        throw new Error('Formato de datos inesperado');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Funciones para manejar filtros guardados
  const fetchSavedFilters = async () => {
    try {
      const response = await fetch('/api/analytics/filtros-guardados/');
      if (response.ok) {
        const result = await response.json();
        setSavedFilters(result.data || []);
      }
    } catch (err) {
      console.error('Error cargando filtros guardados:', err);
    }
  };

  const handleSaveFilter = async () => {
    if (!filterName.trim()) {
      alert('Por favor ingresa un nombre para el filtro');
      return;
    }

    try {
      const csrfToken = getCsrfToken();
      const response = await fetch('/api/analytics/filtros-guardados/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({
          nombre: filterName.trim(),
          descripcion: filterDescription.trim(),
          filtros: activeFilters
        })
      });

      if (response.ok) {
        const result = await response.json();
        setSavedFilters([...savedFilters, result.data]);
        setFilterName('');
        setFilterDescription('');
        setShowSaveFilterModal(false);
        alert('Filtro guardado exitosamente');
      } else {
        const error = await response.json();
        alert('Error guardando filtro: ' + (error.error || 'Error desconocido'));
      }
    } catch (err) {
      alert('Error guardando filtro: ' + err.message);
    }
  };

  const handleDeleteSavedFilter = async (filtroId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este filtro?')) {
      try {
        const csrfToken = getCsrfToken();
        const response = await fetch(`/api/analytics/filtros-guardados/${filtroId}/`, {
          method: 'DELETE',
          headers: {
            'X-CSRFToken': csrfToken,
          },
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          setSavedFilters(savedFilters.filter(f => f.id !== filtroId));
          alert(data.message || 'Filtro eliminado exitosamente');
        } else {
          const errorData = await response.json();
          alert(errorData.error || 'Error eliminando filtro');
        }
      } catch (err) {
        console.error('Error en handleDeleteSavedFilter:', err);
        alert('Error eliminando filtro: ' + err.message);
      }
    }
  };

  useEffect(() => {
    fetchSavedFilters();
    fetchLicitaciones(buildFiltersWithQuery(initialSearchState.filters, initialSearchState.searchQuery), 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    persistSearchState(activeFilters, activeFilters.q || "");
  }, [activeFilters]);

  const applyFilters = (nextFilters = {}, nextQuery = searchQuery) => {
    const mergedFilters = buildFiltersWithQuery(nextFilters, nextQuery);
    setSearchQuery(String(nextQuery || ""));
    setActiveFilters(mergedFilters);
    fetchLicitaciones(mergedFilters, 1);
  };

  const handleLoadFilter = (filtro) => {
    const loadedFilters =
      typeof filtro?.filtros === 'object' && filtro.filtros ? filtro.filtros : {};
    applyFilters(loadedFilters, loadedFilters.q || "");
    setShowLoadFilterModal(false);
  };

  
  // Actualiza el searchQuery cuando cambia mediante el componente SearchTable
  const handleSearchQueryChange = (query) => {
    setSearchQuery(query);
  };

  // Función para eliminar un filtro activo
  const handleRemoveFilter = (filterKey) => {
    const updatedFilters = { ...activeFilters };
    delete updatedFilters[filterKey];
    
    // Si se elimina el filtro de búsqueda, limpiamos también el searchQuery
    if (filterKey === 'q') {
      applyFilters(updatedFilters, '');
      return;
    }
    
    applyFilters(updatedFilters, searchQuery);
  };

  // Funciones de paginación
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= paginationInfo.total_pages) {
      fetchLicitaciones(activeFilters, newPage);
    }
  };



  return (
    <div className="search-page">
      <div className="search-header-container">
        <ParticlesBackground />
        <div className="search-header-content">
          <h1 className="search-title">ENCUENTRA LA LICITACIÓN QUE ESTÁS BUSCANDO</h1>
          <p className="search-subtitle">
            Encuentra las ofertas más similares a tu búsqueda, con esto estás listo para tomar una decisión informada.
          </p>
        </div>
      </div>

      <div className="search-content-container">
        {loading && (
          <div className="search-loading">
            <div className="spinner"></div>
            <span style={{marginLeft: '1rem'}}>Cargando datos...</span>
          </div>
        )}
        {error && (
          <div className="search-error">
            <strong>Error:</strong> {error}
          </div>
        )}
        {!loading && !error && data.length > 0 && (
          <SearchTable
            data={data}
            paginationInfo={paginationInfo}
            onPageChange={handlePageChange}
            searchQuery={searchQuery}
            onSearchQueryChange={handleSearchQueryChange}
            onLoadFilter={() => setShowLoadFilterModal(true)}
            activeFilters={activeFilters}
            onRemoveFilter={handleRemoveFilter}
            onOpenSaveFilter={() => setShowSaveFilterModal(true)}
            onFilter={(filtroBusqueda) => {
              if (typeof filtroBusqueda === 'object' && filtroBusqueda !== null) {
                const nextSearchQuery = filtroBusqueda.q !== undefined ? filtroBusqueda.q : searchQuery;
                applyFilters({ ...activeFilters, ...filtroBusqueda }, nextSearchQuery);
              } else {
                setShowFilters(true);
              }
            }}
          />
        )}
        {!loading && !error && data.length === 0 && (
          <SearchTable
            data={[]}
            paginationInfo={null}
            onPageChange={null}
            searchQuery={searchQuery}
            onSearchQueryChange={handleSearchQueryChange}
            onLoadFilter={() => setShowLoadFilterModal(true)}
            activeFilters={activeFilters}
            onRemoveFilter={handleRemoveFilter}
            onOpenSaveFilter={() => setShowSaveFilterModal(true)}
            onFilter={(filtroBusqueda) => {
              if (typeof filtroBusqueda === 'object' && filtroBusqueda !== null) {
                const nextSearchQuery = filtroBusqueda.q !== undefined ? filtroBusqueda.q : searchQuery;
                applyFilters({ ...activeFilters, ...filtroBusqueda }, nextSearchQuery);
              } else {
                setShowFilters(true);
              }
            }}
          />
        )}
        {showFilters && (
          <div className="modal-overlay modal-overlay-filters" onClick={() => setShowFilters(false)}>
            <div className="modal-content modal-filters-panel" onClick={e => e.stopPropagation()}>
              <SearchForm
                initialFilters={activeFilters}
                onSearch={(filters) => {
                  applyFilters(filters, searchQuery);
                  setShowFilters(false);
                }}
                onCancel={() => setShowFilters(false)}
              />
            </div>
          </div>
        )}
        
        {/* Modal para guardar filtro */}
        {showSaveFilterModal && (
          <div className="modal-overlay" onClick={() => setShowSaveFilterModal(false)}>
            <div className="modal-content modal-filter-actions" onClick={e => e.stopPropagation()}>
              <h3>Guardar Filtro</h3>
              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="filterName" style={{ display: 'block', marginBottom: '0.5rem' }}>
                  Nombre del filtro *
                </label>
                <input
                  id="filterName"
                  type="text"
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                  placeholder="Ej: Licitaciones Bogotá 2024"
                  style={{ 
                    width: '100%', 
                    padding: '0.5rem', 
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '1rem'
                  }}
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="filterDescription" style={{ display: 'block', marginBottom: '0.5rem' }}>
                  Descripción (opcional)
                </label>
                <textarea
                  id="filterDescription"
                  value={filterDescription}
                  onChange={(e) => setFilterDescription(e.target.value)}
                  placeholder="Describe brevemente este filtro..."
                  style={{ 
                    width: '100%', 
                    padding: '0.5rem', 
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    minHeight: '80px',
                    resize: 'vertical'
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowSaveFilterModal(false)}
                >
                  Cancelar
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleSaveFilter}
                >
                  Guardar
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Modal para cargar filtro */}
        {showLoadFilterModal && (
          <div className="modal-overlay" onClick={() => setShowLoadFilterModal(false)}>
            <div className="modal-content modal-filter-actions" onClick={e => e.stopPropagation()}>
              <h3>Cargar Filtro Guardado</h3>
              {savedFilters.length === 0 ? (
                <p>No tienes filtros guardados aún.</p>
              ) : (
                <div>
                  {savedFilters.map((filtro) => (
                    <div 
                      key={filtro.id} 
                      style={{ 
                        border: '1px solid #ddd', 
                        borderRadius: '4px', 
                        padding: '1rem', 
                        marginBottom: '1rem',
                        backgroundColor: '#f9f9f9'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>{filtro.nombre}</h4>
                          {filtro.descripcion && (
                            <p style={{ margin: '0 0 0.5rem 0', color: '#666', fontSize: '0.9rem' }}>
                              {filtro.descripcion}
                            </p>
                          )}
                          <p style={{ margin: '0', fontSize: '0.8rem', color: '#888' }}>
                            Creado: {new Date(filtro.fecha_creacion).toLocaleDateString('es-ES')}
                          </p>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button 
                            className="btn btn-primary"
                            onClick={() => handleLoadFilter(filtro)}
                            style={{ padding: '0.25rem 0.75rem', fontSize: '0.875rem' }}
                          >
                            Cargar
                          </button>
                          <button 
                            className="btn btn-danger"
                            onClick={() => handleDeleteSavedFilter(filtro.id)}
                            style={{ padding: '0.25rem 0.75rem', fontSize: '0.875rem' }}
                          >
                            Eliminar
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowLoadFilterModal(false)}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Search;
